import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/** Every user is logged out after this long without activity in any tab of the browser. */
export const SESSION_IDLE_TIMEOUT_MS = 60 * 60_000;

/**
 * How long a rejected renewal or session check waits before ending the
 * session. Two tabs sharing tokens can renew at the same moment; the server
 * lets one win, and the loser must give the winner's tokens time to land in
 * storage instead of logging both tabs out.
 */
export const SESSION_SETTLE_MS = 2_000;

/** User activity shared by every tab of this browser through localStorage. */
@Injectable({ providedIn: 'root' })
export class UserActivityService {
    private readonly document = inject(DOCUMENT);
    private readonly storageKey = 'qc:last-activity';
    private ownActivity = -Infinity;
    private lastBroadcast = -Infinity;

    record(now = Date.now()): void {
        this.ownActivity = now;
        // Other tabs only need second-level precision; avoid a write per mouse move.
        if (now - this.lastBroadcast < 5_000) return;
        this.lastBroadcast = now;
        try {
            this.document.defaultView?.localStorage.setItem(this.storageKey, String(now));
        } catch { /* Storage unavailable: this tab still tracks its own activity. */ }
    }

    /** Most recent user activity in this tab or any other tab of the browser. */
    last(): number {
        let shared = -Infinity;
        try {
            shared = Number(this.document.defaultView?.localStorage.getItem(this.storageKey)) || -Infinity;
        } catch { /* Fall back to this tab's own activity. */ }
        return Math.max(this.ownActivity, shared);
    }

    /** True only once activity has been seen and then stopped for the full idle limit. */
    isIdle(now = Date.now()): boolean {
        const last = this.last();
        return Number.isFinite(last) && now - last > SESSION_IDLE_TIMEOUT_MS;
    }
}
